import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, onSnapshot, setDoc } from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';

export interface SessionContextType {
    currentUser: string | null;
    setCurrentUser: (name: string | null) => void;
    logout: () => Promise<void>;
    showPortalGuru: boolean;
    togglePortalGuru: (value: boolean) => Promise<void>;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const useSession = () => {
    const context = useContext(SessionContext);
    if (!context) throw new Error('useSession must be used within SessionProvider');
    return context;
};

export const SessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { authUser, logout: authLogout } = useAuth();

    const [currentUser, setCurrentUser] = useState<string | null>(
        () => sessionStorage.getItem('currentUser')
    );
    const [showPortalGuru, setShowPortalGuru] = useState<boolean>(false);

    // Note: Petugas clinic session logic removed to unify user identity under GuruContext

    // Reset currentUser if authUser hilang
    useEffect(() => {
        if (!authUser) {
            handleSetCurrentUser(null);
        }
    }, [authUser]);

    // Subscribe feature_toggles (showPortalGuru)
    useEffect(() => {
        if (!authUser) return;
        const featureTogglesDocRef = doc(db, 'settings', 'feature_toggles');
        const unsubscribe = onSnapshot(featureTogglesDocRef, (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                if (typeof data.showPortalGuru === 'boolean') {
                    setShowPortalGuru(data.showPortalGuru);
                }
            }
        });
        return () => unsubscribe();
    }, [authUser]);

    const handleSetCurrentUser = (name: string | null) => {
        if (name) {
            sessionStorage.setItem('currentUser', name);
        } else {
            sessionStorage.removeItem('currentUser');
        }
        setCurrentUser(name);
    };

    const handleLogout = async () => {
        await authLogout();
        handleSetCurrentUser(null);
    };

    const togglePortalGuru = async (value: boolean) => {
        await setDoc(doc(db, 'settings', 'feature_toggles'), { showPortalGuru: value }, { merge: true });
        setShowPortalGuru(value);
    };

    return (
        <SessionContext.Provider value={{
            currentUser,
            setCurrentUser: handleSetCurrentUser,
            logout: handleLogout,
            showPortalGuru,
            togglePortalGuru,
        }}>
            {children}
        </SessionContext.Provider>
    );
};
